import { useCallback, useEffect, useState } from "react";
import { Button, Modal, Popconfirm, Progress } from "antd";

interface ProgressState {
  open: boolean;
  fileName: string;
  fileIndex: number;
  totalFiles: number;
  currentPercent: number;
  totalPercent: number;
}

const INITIAL: ProgressState = {
  open: false,
  fileName: "",
  fileIndex: 0,
  totalFiles: 0,
  currentPercent: 0,
  totalPercent: 0,
};

/**
 * 진행 모달 z-index.
 * 고정 헤더(z-index 9999)와 모바일 메뉴(11000~11001)보다 위에 둬서
 * 업로드 중 헤더 메뉴로 페이지를 옮기지 못하게 한다 (옮기면 진행 상황·중지 버튼이 사라진 채 업로드만 계속된다).
 */
const PROGRESS_MODAL_Z_INDEX = 12000;

/** open 동안 탭 닫기·새로고침·외부 이동 시 브라우저 기본 확인창을 띄운다 */
const useWarnBeforeUnload = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // 구형 브라우저 호환 (문구는 브라우저 기본 문구로 표시된다)
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);
};

const toPercent = (ratio: number) => Number((Math.min(Math.max(ratio, 0), 1) * 100).toFixed(2));

export const useUploadProgress = () => {
  const [state, setState] = useState<ProgressState>(INITIAL);

  const start = useCallback((totalFiles: number) => {
    setState({ ...INITIAL, open: true, totalFiles });
  }, []);

  const startFile = useCallback((index: number, fileName: string) => {
    setState((prev) => ({
      ...prev,
      fileName,
      fileIndex: index + 1,
      currentPercent: 0,
      totalPercent: toPercent(index / (prev.totalFiles || 1)),
    }));
  }, []);

  const updateFile = useCallback((index: number, ratio: number) => {
    setState((prev) => ({
      ...prev,
      currentPercent: toPercent(ratio),
      totalPercent: toPercent((index + ratio) / (prev.totalFiles || 1)),
    }));
  }, []);

  const finish = useCallback(() => setState(INITIAL), []);

  return { progress: state, start, startFile, updateFile, finish };
};

interface Props {
  open: boolean;
  fileName: string;
  fileIndex: number;
  totalFiles: number;
  currentPercent: number;
  totalPercent: number;
  compactSingleFile?: boolean;
  /** 넘기면 "업로드 중지" 버튼을 보여준다 (확인 후 호출) */
  onCancel?: () => void;
  /** 중지 요청 후 정리 중 */
  cancelling?: boolean;
}

const UploadProgressModal = ({
  open,
  fileName,
  fileIndex,
  totalFiles,
  currentPercent,
  totalPercent,
  compactSingleFile = false,
  onCancel,
  cancelling = false,
}: Props) => {
  const showTotal = !compactSingleFile || totalFiles > 1;
  useWarnBeforeUnload(open);

  return (
    <Modal
      centered
      zIndex={PROGRESS_MODAL_Z_INDEX}
      title="파일 업로드 중"
      footer={
        onCancel ? (
          <Popconfirm
            title="업로드를 중지할까요?"
            description={
              <div style={{ maxWidth: 280 }}>
                전송 중인 파일과 남은 파일은 올라가지 않습니다.
                <br />
                이미 전송이 끝난 파일은 그대로 등록됩니다.
              </div>
            }
            okText="중지"
            okButtonProps={{ danger: true }}
            cancelText="계속 업로드"
            onConfirm={onCancel}
            disabled={cancelling}
          >
            <Button danger loading={cancelling}>
              {cancelling ? "중지하는 중..." : "업로드 중지"}
            </Button>
          </Popconfirm>
        ) : null
      }
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
    >
      <div style={{ marginBottom: "16px" }}>
        <div style={{ marginBottom: "8px", color: "#595959" }}>
          업로드 중: {fileName}
        </div>
        {showTotal && (
          <div style={{ marginBottom: "8px", color: "#8c8c8c", fontSize: "12px" }}>
            {fileIndex} / {totalFiles} 파일
          </div>
        )}
      </div>
      {showTotal && (
        <>
          <div style={{ marginTop: "20px", marginBottom: "8px", color: "#595959", fontSize: "13px" }}>
            전체 진행률:
          </div>
          <Progress percent={totalPercent} status="active" />
        </>
      )}
      <div style={{ marginBottom: "8px", color: "#595959", fontSize: "13px" }}>
        {showTotal ? "현재 파일 진행률:" : "진행률:"}
      </div>
      <Progress percent={currentPercent} status="active" />
    </Modal>
  );
};

export default UploadProgressModal;
